import { useState, useCallback, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { passServerFetch } from '@/lib/pass-server';
import { getDashboardTypeFromPath } from '@/lib/constants/dashboard';
import { toast } from '@/hooks/ui/use-toast';
import { shiftQueryKeys } from '@/hooks/scheduling/useShiftsQuery';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useOnboardingSafe } from '@/context/OnboardingContext';
import type { AIMessage, AIResponse, AIConversationSummary, AIUsage, AIToolCall, AIToolResult, PendingTool, AIQuestion, AIQuestionAnswers, StreamEvent } from './types';
import type { ParsedFileData } from '@/lib/ai-chat/file-parser';

// Query keys for AI chat
export const aiChatQueryKeys = {
  conversations: (businessId: string | null) => ['ai-conversations', businessId] as const,
  messages: (businessId: string | null, conversationId: string | null) =>
    ['ai-messages', businessId, conversationId] as const,
};

// LocalStorage key for persisting active conversation per business
const getActiveConversationKey = (businessId: string) => `ai_active_conversation_${businessId}`;

// LocalStorage key for auto-accept mode per business
const getAutoAcceptKey = (businessId: string) => `ai_auto_accept_${businessId}`;

// Helper to generate conversation title from first message
const generateTitle = (content: string): string => {
  const maxLength = 40;

  // Clean up the message
  let cleaned = content.trim().replace(/\s+/g, ' ');

  // Remove common question starters for cleaner titles
  const questionStarters = [
    /^(can you|could you|please|hey|hi|hello|i want to|i need to|i'd like to|show me|tell me|help me|what is|what are|what's|how do|how can|how is|how are)\s+/i,
    /^(give me|get me|find|search|look up|check)\s+/i,
  ];

  for (const pattern of questionStarters) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

  // Remove trailing punctuation for cleaner look
  cleaned = cleaned.replace(/[?.!,]+$/, '');

  if (cleaned.length <= maxLength) return cleaned;

  // Try to cut at word boundary
  const truncated = cleaned.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.6) {
    return truncated.substring(0, lastSpace) + '...';
  }

  return truncated + '...';
};

// Tools that modify data and require cache invalidation
// Maps resource type to tool names that modify that resource
const DATA_MODIFYING_TOOLS = {
  shifts: ['create_shift', 'update_shift', 'delete_shift', 'apply_shift_template', 'apply_day_template'],
  employees: ['create_employee', 'update_employee', 'delete_employee'],
  roles: ['create_role', 'update_role', 'delete_role'],
  reviews: ['create_review', 'update_review', 'delete_review'],
  openingHours: ['update_opening_hours'],
  closedDates: ['add_closed_date', 'remove_closed_date'],
  businessSettings: ['update_business_settings'],
  shiftTemplates: ['create_shift_template', 'update_shift_template', 'delete_shift_template'],
  dayTemplates: ['create_day_template', 'update_day_template', 'delete_day_template'],
  notifications: ['send_pass_notification'],
} as const;

// Resource types that can be modified
interface ModifiedResources {
  shifts: boolean;
  employees: boolean;
  roles: boolean;
  reviews: boolean;
  openingHours: boolean;
  closedDates: boolean;
  businessSettings: boolean;
  shiftTemplates: boolean;
  dayTemplates: boolean;
  notifications: boolean;
}

// Helper to detect which resources were modified by tool calls
function getModifiedResources(toolCalls: AIToolCall[] | undefined): ModifiedResources {
  const result: ModifiedResources = {
    shifts: false,
    employees: false,
    roles: false,
    reviews: false,
    openingHours: false,
    closedDates: false,
    businessSettings: false,
    shiftTemplates: false,
    dayTemplates: false,
    notifications: false,
  };

  if (!toolCalls) return result;

  const hasSuccessfulCall = (tools: readonly string[]) =>
    toolCalls.some(tc => tools.includes(tc.name) && tc.result?.success);

  for (const key of Object.keys(DATA_MODIFYING_TOOLS) as (keyof typeof DATA_MODIFYING_TOOLS)[]) {
    result[key] = hasSuccessfulCall(DATA_MODIFYING_TOOLS[key]);
  }

  return result;
}

// Emit custom event for components that don't use React Query
function emitDataModifiedEvent(resources: ModifiedResources, businessId: string) {
  const modifiedResourceNames = Object.entries(resources)
    .filter(([, modified]) => modified)
    .map(([name]) => name);

  if (modifiedResourceNames.length > 0) {
    window.dispatchEvent(new CustomEvent('ai-data-modified', {
      detail: { resources: modifiedResourceNames, businessId }
    }));
    console.log('[AI Chat] Emitting ai-data-modified event for:', modifiedResourceNames);
  }
}

// Invalidate React Query caches for modified resources
function invalidateModifiedResources(
  queryClient: ReturnType<typeof useQueryClient>,
  resources: ModifiedResources,
  businessId: string
) {
  const anyModified = Object.values(resources).some(v => v);
  if (!anyModified || !businessId) return;

  console.log('[AI Chat] Invalidating caches for modified resources:',
    Object.entries(resources).filter(([, v]) => v).map(([k]) => k));

  // Shifts
  if (resources.shifts) {
    queryClient.invalidateQueries({
      queryKey: shiftQueryKeys.all,
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key.includes(businessId);
      },
    });
  }

  // Employees
  if (resources.employees) {
    queryClient.invalidateQueries({ queryKey: ['employees', businessId] });
    queryClient.invalidateQueries({ queryKey: ['employment-types', businessId] });
    queryClient.invalidateQueries({ queryKey: ['plan-fulfillment', businessId] });
  }

  // Roles (job positions)
  if (resources.roles) {
    queryClient.invalidateQueries({ queryKey: ['employment-types', businessId] });
    queryClient.invalidateQueries({ queryKey: ['role-hierarchy', businessId] });
  }

  // Reviews
  if (resources.reviews) {
    queryClient.invalidateQueries({ queryKey: ['reviews', businessId] });
  }

  // Business settings & hours
  if (resources.openingHours || resources.closedDates || resources.businessSettings) {
    // These typically trigger a refetch of business data
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) &&
          (key[0] === 'business' || key[0] === 'opening-hours' || key[0] === 'closed-dates') &&
          key.includes(businessId);
      }
    });
  }

  // Notifications (invalidate loyalty-related caches)
  if (resources.notifications) {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) &&
          (key[0] === 'loyalty' || key[0] === 'pass-notifications' || key[0] === 'loyalty-programs') &&
          key.includes(businessId);
      }
    });
  }
}

// Tool display names for toast messages
const TOOL_DISPLAY_NAMES: Record<string, { action: string; resource: string }> = {
  // Shifts
  create_shift: { action: 'created', resource: 'Shift' },
  update_shift: { action: 'updated', resource: 'Shift' },
  delete_shift: { action: 'deleted', resource: 'Shift' },
  get_shifts: { action: 'fetched', resource: 'Shifts' },

  // Employees
  create_employee: { action: 'created', resource: 'Employee' },
  update_employee: { action: 'updated', resource: 'Employee' },
  delete_employee: { action: 'deleted', resource: 'Employee' },
  list_employees: { action: 'fetched', resource: 'Employees' },
  get_employee_plan_fulfillment: { action: 'fetched', resource: 'Plan fulfillment' },

  // Roles (job positions)
  create_role: { action: 'created', resource: 'Role' },
  update_role: { action: 'updated', resource: 'Role' },
  delete_role: { action: 'deleted', resource: 'Role' },
  list_roles: { action: 'fetched', resource: 'Roles' },
  get_role: { action: 'fetched', resource: 'Role' },

  // Reviews
  get_reviews: { action: 'fetched', resource: 'Reviews' },

  // Business
  get_business_stats: { action: 'fetched', resource: 'Business stats' },
  update_opening_hours: { action: 'updated', resource: 'Opening hours' },
  add_closed_date: { action: 'added', resource: 'Closed date' },
  remove_closed_date: { action: 'removed', resource: 'Closed date' },
  update_business_settings: { action: 'updated', resource: 'Business settings' },
  get_opening_hours: { action: 'fetched', resource: 'Opening hours' },
  get_closed_dates: { action: 'fetched', resource: 'Closed dates' },
  get_business_settings: { action: 'fetched', resource: 'Business settings' },

  // Loyalty & Notifications
  list_loyalty_programs: { action: 'fetched', resource: 'Loyalty programs' },
  send_pass_notification: { action: 'sent', resource: 'Pass notification' },
};

// Show toast notifications for tool call results
const showToolResultToasts = (toolCalls: AIToolCall[] | undefined) => {
  if (!toolCalls || toolCalls.length === 0) return;

  for (const tc of toolCalls) {
    const toolInfo = TOOL_DISPLAY_NAMES[tc.name];
    if (!toolInfo) continue; // Skip unknown tools

    if (tc.error) {
      // Show error toast
      toast.error(`${toolInfo.resource} error`, {
        description: tc.error,
      });
    } else if (tc.result?.success !== undefined) {
      // Only show success toast for mutation operations (create/update/delete)
      if (tc.result.success && tc.name.match(/^(create|update|delete|send)_/)) {
        toast.success(`${toolInfo.resource} ${toolInfo.action}`, {
          description: tc.result.message || `${toolInfo.resource} was ${toolInfo.action} successfully`,
        });
      } else if (!tc.result.success) {
        toast.error(`Failed to ${tc.name.replace('_', ' ')}`, {
          description: tc.result.message || 'An error occurred',
        });
      }
    }
  }
};

interface DbMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  tool_calls: Record<string, unknown>[] | null;
  tool_results: Record<string, unknown>[] | null;
  created_at: string;
}

// Map tab IDs to human-readable names
const TAB_DISPLAY_NAMES: Record<string, string> = {
  'insights': 'Dashboard Insights',
  'reviews': 'Reviews',
  'loyalty': 'Loyalty Program',
  'wallet': 'Digital Wallet',
  'coupons': 'Coupons',
  'qr-codes': 'QR Codes',
  'bulk-mail': 'Bulk Mail',
  'employees': 'Employee Management',
  'shifts': 'Shift Management',
  'shift-management': 'Shift Management',
  'time-tracking': 'Time Tracking',
  'employee-list': 'Employees',
  'payroll': 'Payroll',
  'recruitment': 'Recruitment',
  'analytics': 'Analytics',
  'communications': 'Communications',
  'meetings': 'Meetings',
  'meetings-calendar': 'Meetings',
  'meetings-pages': 'Pages',
  'business-settings': 'Business Settings',
  'my-dashboard': 'My Dashboard',
  'my-shifts': 'My Shifts',
  'my-time': 'Time Tracking',
  'my-payroll': 'My Payroll',
  'my-events': 'My Events',
  'conversations': 'WhatsApp',
  'social': 'Social Media',
  'social-accounts': 'Social Accounts',
  'social-posts': 'Social Posts',
  'social-inbox': 'Social Inbox',
  'social-analytics': 'Social Analytics',
  'social-auto-reply': 'Auto Reply',
  'form-builder': 'Form Builder',
  'forms': 'Review Forms',
  'google': 'Google Reviews',
  'feedback-intelligence': 'AI Feedback Intel',
  'customers': 'Customers',
  'email-templates': 'Email Templates',
  'signage': 'Digital Signage',
  'overview': 'Overview',
  'schedule': 'Schedule',
  'alerts': 'Alerts',
};

// Map tab IDs to icon names (matching sidebar.tsx icons)
export const TAB_ICON_NAMES: Record<string, string> = {
  'insights': 'LineChart',
  'reviews': 'Star',
  'loyalty': 'Award',
  'wallet': 'Tag',
  'coupons': 'Gift',
  'qr-codes': 'ClipboardList',
  'bulk-mail': 'Mail',
  'email-templates': 'Mail',
  'employees': 'Users',
  'employee-list': 'Users',
  'shifts': 'Clock',
  'shift-management': 'Clock',
  'time-tracking': 'LineChart',
  'payroll': 'DollarSign',
  'recruitment': 'Briefcase',
  'analytics': 'BarChart3',
  'communications': 'MessagesSquare',
  'meetings': 'CalendarCheck',
  'meetings-calendar': 'CalendarCheck',
  'meetings-pages': 'FileText',
  'business-settings': 'Settings',
  'my-dashboard': 'Clock',
  'my-shifts': 'Calendar',
  'my-time': 'Clock',
  'my-payroll': 'DollarSign',
  'my-events': 'CalendarDays',
  'conversations': 'MessagesSquare',
  'social': 'Share2',
  'social-accounts': 'Share2',
  'social-posts': 'Send',
  'social-inbox': 'Inbox',
  'social-analytics': 'TrendingUp',
  'social-auto-reply': 'MessageSquareReply',
  'form-builder': 'ClipboardList',
  'forms': 'ClipboardList',
  'google': 'Globe',
  'feedback-intelligence': 'Brain',
  'customers': 'Users',
  'signage': 'Monitor',
  'overview': 'LayoutDashboard',
  'schedule': 'Calendar',
  'alerts': 'Bell',
};

export const useAIChat = (
  businessId: string | null,
  accessToken?: string
) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const { businessDashboardTab, employeeDashboardTab, employeePortalTab, franchiseDashboardTab } = useDashboardTab();
  const onboardingState = useOnboardingSafe();

  // Initialize activeConversationId from localStorage
  const [activeConversationId, setActiveConversationId] = useState<string | null>(() => {
    if (!businessId) return null;
    try {
      return localStorage.getItem(getActiveConversationKey(businessId));
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<AIUsage | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const [wasStopped, setWasStopped] = useState(false);
  const [streamingContent, setStreamingContent] = useState<string>('');

  // Auto-accept mode - automatically confirms pending tools without user interaction
  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState<boolean>(() => {
    if (!businessId) return false;
    try {
      return localStorage.getItem(getAutoAcceptKey(businessId)) === 'true';
    } catch {
      return false;
    }
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  // Track the previous businessId to detect changes
  const prevBusinessIdRef = useRef<string | null>(businessId);
  // Ref to always get latest sendMessage (avoids stale closure in setTimeout)
  const sendMessageRef = useRef<typeof sendMessage | null>(null);

  // Get current context based on route and tab
  const getCurrentContext = useCallback(() => {
    const path = location.pathname;
    const dashboardType = getDashboardTypeFromPath(path);
    let dashboard: 'business' | 'employee' | 'franchise' | 'portal' = 'business';
    let tab = 'insights';

    switch (dashboardType) {
      case 'employee-portal':
        dashboard = 'portal';
        tab = employeePortalTab;
        break;
      case 'employee':
        dashboard = 'employee';
        tab = employeeDashboardTab;
        break;
      case 'franchise':
        dashboard = 'franchise';
        tab = franchiseDashboardTab;
        break;
      case 'business':
        dashboard = 'business';
        tab = businessDashboardTab;
        break;
    }

    return {
      tab,
      tabDisplayName: TAB_DISPLAY_NAMES[tab] || tab,
      tabIconName: TAB_ICON_NAMES[tab] || null,
      dashboard,
      route: path,
    };
  }, [location.pathname, businessDashboardTab, employeeDashboardTab, employeePortalTab, franchiseDashboardTab]);

  // React Query for conversations - keyed by businessId
  const {
    data: conversations = [],
    isLoading: isLoadingConversations,
  } = useQuery({
    queryKey: aiChatQueryKeys.conversations(businessId),
    queryFn: async (): Promise<AIConversationSummary[]> => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('ai_conversations')
        .select(`
          id,
          title,
          created_at,
          updated_at,
          forked_from_conversation_id,
          forked_from_message_id,
          ai_messages!conversation_id(count)
        `)
        .eq('business_id', businessId)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((conv: any) => ({
        id: conv.id,
        title: conv.title,
        createdAt: conv.created_at,
        updatedAt: conv.updated_at,
        messageCount: conv.ai_messages?.[0]?.count || 0,
        forkedFromConversationId: conv.forked_from_conversation_id,
        forkedFromMessageId: conv.forked_from_message_id,
      }));
    },
    enabled: !!businessId,
    staleTime: 30000, // 30 seconds
  });

  // React Query for messages - keyed by businessId + conversationId
  const {
    data: messages = [],
  } = useQuery({
    queryKey: aiChatQueryKeys.messages(businessId, activeConversationId),
    queryFn: async (): Promise<AIMessage[]> => {
      if (!activeConversationId) return [];

      const { data, error } = await supabase
        .from('ai_messages')
        .select('*')
        .eq('conversation_id', activeConversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return (data || []).map((msg: DbMessage) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at,
        toolCalls: msg.tool_calls as AIToolCall[] | undefined,
        toolResults: msg.tool_results as AIToolResult[] | undefined,
      }));
    },
    enabled: !!businessId && !!activeConversationId,
    staleTime: 10000, // 10 seconds
  });

  // Process SSE streaming response
  const processStreamingResponse = useCallback(async (
    response: Response,
    assistantMessageId: string,
    currentConvId: string
  ): Promise<{ content: string; toolCalls: AIToolCall[]; toolResults: AIToolResult[]; pendingTools: PendingTool[]; questions: AIQuestion[]; showUser: AIResponse['showUser'] }> => {
    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let content = '';
    let toolCalls: AIToolCall[] = [];
    let toolResults: AIToolResult[] = [];
    let pendingTools: PendingTool[] = [];
    let questions: AIQuestion[] = [];
    let showUser: AIResponse['showUser'] = undefined;
    let buffer = ''; // Buffer for incomplete SSE lines

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6); // Remove 'data: ' prefix
          if (data === '[DONE]') continue;

          try {
            const event: StreamEvent = JSON.parse(data);

            switch (event.type) {
              case 'content':
                if (event.content) {
                  content += event.content;
                  // Use flushSync to force immediate render for streaming effect
                  flushSync(() => {
                    setStreamingContent(content);
                  });
                }
                break;

              case 'tool_start':
                console.log('[AI Chat] Tool execution started:', event.tools);
                break;

              case 'tool_result':
                if (event.tool && event.result) {
                  toolResults.push(event.result);
                }
                break;

              case 'pending_tools':
                if (event.pendingTools) {
                  pendingTools = event.pendingTools;
                }
                break;

              case 'done':
                if (event.response) content = event.response;
                if (event.toolCalls) toolCalls = event.toolCalls;
                if (event.pendingTools) pendingTools = event.pendingTools;
                if (event.questions) questions = event.questions;
                if (event.showUser) showUser = event.showUser;
                break;

              case 'error':
                throw new Error(event.error || 'Streaming error');
            }
          } catch (parseError) {
            // Ignore parse errors in stream - might be partial data
          }
        }
      }

      // Process any remaining buffered data
      if (buffer.startsWith('data: ')) {
        const data = buffer.slice(6);
        if (data && data !== '[DONE]') {
          try {
            const event: StreamEvent = JSON.parse(data);
            if (event.type === 'done') {
              if (event.response) content = event.response;
              if (event.toolCalls) toolCalls = event.toolCalls;
              if (event.pendingTools) pendingTools = event.pendingTools;
              if (event.questions) questions = event.questions;
              if (event.showUser) showUser = event.showUser;
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    } finally {
      reader.releaseLock();
      setStreamingContent(''); // Clear streaming content to prevent memory leak
    }

    return { content, toolCalls, toolResults, pendingTools, questions, showUser };
  }, []);

  // Reset active conversation when business changes - load from localStorage
  useEffect(() => {
    if (businessId !== prevBusinessIdRef.current) {
      prevBusinessIdRef.current = businessId;
      if (businessId) {
        try {
          const savedId = localStorage.getItem(getActiveConversationKey(businessId));
          setActiveConversationId(savedId);
        } catch {
          setActiveConversationId(null);
        }
      } else {
        setActiveConversationId(null);
      }
    }
  }, [businessId]);

  // Persist activeConversationId to localStorage
  useEffect(() => {
    if (businessId && activeConversationId) {
      try {
        localStorage.setItem(getActiveConversationKey(businessId), activeConversationId);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [businessId, activeConversationId]);

  // Auto-load onboarding conversation if one exists and no conversation is active
  useEffect(() => {
    if (!businessId || activeConversationId) return;
    const onboardingConvId = onboardingState?.onboardingConversationId;
    if (onboardingConvId) {
      setActiveConversationId(onboardingConvId);
    }
  }, [businessId, activeConversationId, onboardingState?.onboardingConversationId]);

  // Persist autoAcceptEnabled to localStorage
  useEffect(() => {
    if (businessId) {
      try {
        localStorage.setItem(getAutoAcceptKey(businessId), autoAcceptEnabled.toString());
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [businessId, autoAcceptEnabled]);

  // Reset auto-accept state when business changes
  useEffect(() => {
    if (businessId) {
      try {
        const saved = localStorage.getItem(getAutoAcceptKey(businessId));
        setAutoAcceptEnabled(saved === 'true');
      } catch {
        setAutoAcceptEnabled(false);
      }
    } else {
      setAutoAcceptEnabled(false);
    }
  }, [businessId]);

  // Auto-select conversation when conversations load
  useEffect(() => {
    if (conversations.length === 0) return;

    // If we have an active ID, verify it still exists in the list
    if (activeConversationId) {
      const exists = conversations.some(c => c.id === activeConversationId);
      if (exists) return; // Current selection is valid
    }

    // Select the first conversation if no valid selection
    setActiveConversationId(conversations[0].id);
  }, [conversations, activeConversationId]);

  // Create a new conversation
  const createNewConversation = useCallback(async () => {
    if (!businessId) return null;

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return null;

      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          business_id: businessId,
          user_id: userData.user.id,
          title: 'New Chat',
        })
        .select()
        .single();

      if (error) throw error;

      const newSummary: AIConversationSummary = {
        id: data.id,
        title: data.title,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        messageCount: 0,
      };

      // Optimistically update the conversations cache
      queryClient.setQueryData<AIConversationSummary[]>(
        aiChatQueryKeys.conversations(businessId),
        (old) => [newSummary, ...(old || [])]
      );

      // Set active conversation (messages will load automatically via React Query)
      setActiveConversationId(data.id);

      return data.id;
    } catch (err) {
      console.error('Error creating conversation:', err);
      toast.error('Failed to create new chat');
      return null;
    }
  }, [businessId, queryClient]);

  // Switch to a different conversation
  const switchConversation = useCallback((conversationId: string) => {
    if (conversations.some(c => c.id === conversationId)) {
      setActiveConversationId(conversationId);
    }
  }, [conversations]);

  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId: string) => {
    if (!businessId) return;

    try {
      const { error } = await supabase
        .from('ai_conversations')
        .delete()
        .eq('id', conversationId);

      if (error) throw error;

      // Optimistically update the conversations cache
      queryClient.setQueryData<AIConversationSummary[]>(
        aiChatQueryKeys.conversations(businessId),
        (old) => (old || []).filter(c => c.id !== conversationId)
      );

      // If we deleted the active conversation, switch to another
      if (activeConversationId === conversationId) {
        const remaining = conversations.filter(c => c.id !== conversationId);
        setActiveConversationId(remaining.length > 0 ? remaining[0].id : null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      toast.error('Failed to delete conversation');
    }
  }, [businessId, conversations, activeConversationId, queryClient]);

  // Send a message with optional structured answers
  // forceNewConversation: when true, always creates a new conversation (used by floating bubble)
  const sendMessage = useCallback(
    async (content: string, structuredAnswersOrFile?: AIQuestionAnswers | ParsedFileData, forceNewConversation?: boolean) => {
      // Determine if second arg is file data or structured answers
      const isFileData = !!structuredAnswersOrFile && typeof structuredAnswersOrFile === 'object' && 'fileName' in structuredAnswersOrFile;
      const attachedFile = isFileData ? structuredAnswersOrFile as ParsedFileData : undefined;
      const structuredAnswers = isFileData ? undefined : structuredAnswersOrFile as AIQuestionAnswers | undefined;
      if (!businessId) {
        toast.error('No business selected', {
          description: 'Please select a business first',
        });
        return;
      }

      if (!accessToken) {
        toast.error('Authentication required', {
          description: 'Please sign in to use the AI assistant',
        });
        return;
      }

      const trimmedContent = content.trim();
      if (!trimmedContent) return;

      if (trimmedContent.length > 2000) {
        toast.error('Message too long', {
          description: 'Please keep your message under 2000 characters',
        });
        return;
      }

      // Create new conversation if none active or if forced
      let currentConvId = forceNewConversation ? null : activeConversationId;

      if (!currentConvId) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) {
          toast.error('Authentication required');
          return;
        }

        try {
          const { data, error } = await supabase
            .from('ai_conversations')
            .insert({
              business_id: businessId,
              user_id: userData.user.id,
              title: generateTitle(trimmedContent),
            })
            .select()
            .single();

          if (error) throw error;

          currentConvId = data.id;
          const newSummary: AIConversationSummary = {
            id: data.id,
            title: data.title,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            messageCount: 0,
          };
          // Optimistically update the conversations cache
          queryClient.setQueryData<AIConversationSummary[]>(
            aiChatQueryKeys.conversations(businessId),
            (old) => [newSummary, ...(old || [])]
          );
          setActiveConversationId(currentConvId);

          // Link conversation to onboarding if in guided mode
          if (onboardingState?.onboardingMode === 'guided' && !onboardingState.onboardingConversationId) {
            onboardingState.startAIOnboarding(currentConvId);
          }
        } catch (err) {
          console.error('Error creating conversation:', err);
          toast.error('Failed to start conversation');
          return;
        }
      }

      // Add user message to database
      const userMessageId = crypto.randomUUID();
      const userMessage: AIMessage = {
        id: userMessageId,
        role: 'user',
        content: trimmedContent,
        timestamp: new Date().toISOString(),
        questionAnswers: structuredAnswers, // Include raw answers if provided
      };

      // Optimistically add to UI
      queryClient.setQueryData<AIMessage[]>(
        aiChatQueryKeys.messages(businessId, currentConvId),
        (old) => [...(old || []), userMessage]
      );

      try {
        // Save user message to database
        const { error: msgError } = await supabase
          .from('ai_messages')
          .insert({
            id: userMessageId,
            conversation_id: currentConvId,
            role: 'user',
            content: trimmedContent,
          });

        if (msgError) throw msgError;

        // Update conversation title if first message
        const isFirstMessage = messages.length === 0;
        if (isFirstMessage) {
          await supabase
            .from('ai_conversations')
            .update({ title: generateTitle(trimmedContent) })
            .eq('id', currentConvId);

          // Update local state
          queryClient.setQueryData<AIConversationSummary[]>(
            aiChatQueryKeys.conversations(businessId),
            (old) =>
              (old || []).map(c =>
                c.id === currentConvId
                  ? { ...c, title: generateTitle(trimmedContent) }
                  : c
              )
          );
        }
      } catch (err) {
        console.error('Error saving user message:', err);
      }

      setIsLoading(true);
      setError(null);
      setWasStopped(false);
      setLastFailedMessage(trimmedContent);

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const response = await passServerFetch('/api/ai-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmedContent,
            businessId,
            conversationId: currentConvId,
            conversationHistory: [...messages, userMessage].slice(-10),
            structuredAnswers,
            stream: true,
            onboardingContext: onboardingState ? {
              mode: onboardingState.onboardingMode,
              appTourCompleted: onboardingState.appTourCompleted,
              completedSteps: onboardingState.completedSteps,
              tabToursSeen: onboardingState.tabToursSeen,
            } : undefined,
            fileData: attachedFile ? {
              fileName: attachedFile.fileName,
              fileType: attachedFile.fileType,
              headers: attachedFile.headers,
              rows: attachedFile.rows,
              rowCount: attachedFile.rowCount,
            } : undefined,
          }),
          signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        let data: AIResponse;
        const assistantMessageId = crypto.randomUUID();
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/event-stream')) {
          // Process streaming response
          const streamResult = await processStreamingResponse(response, assistantMessageId, currentConvId);
          data = {
            response: streamResult.content,
            toolCalls: streamResult.toolCalls.length > 0 ? streamResult.toolCalls : undefined,
            toolResults: streamResult.toolResults.length > 0 ? streamResult.toolResults : undefined,
            pendingTools: streamResult.pendingTools.length > 0 ? streamResult.pendingTools : undefined,
            questions: streamResult.questions.length > 0 ? streamResult.questions : undefined,
            showUser: streamResult.showUser,
          };
        } else {
          // Process regular JSON response
          data = await response.json();
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        // Generate appropriate content based on what we have
        let messageContent = data?.response || '';
        if (!messageContent) {
          // No text response - generate contextual fallback
          if (data?.pendingTools?.length) {
            // Pending tools have their own UI section, no need for extra text
            messageContent = '';
          } else if (data?.toolCalls?.length) {
            // Tool calls were executed, the results UI will show them
            messageContent = '';
          } else {
            // Truly empty response
            messageContent = 'No response received';
          }
        }

        const assistantMessage: AIMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: messageContent,
          timestamp: new Date().toISOString(),
          toolCalls: data?.toolCalls,
          toolResults: data?.toolResults, // Structured tool results for proper history
          pendingTools: data?.pendingTools,
          questions: data?.questions, // Include structured questions from ask_user tool
          showUser: data?.showUser,   // Include navigation/highlight instruction
        };

        // Track usage stats
        if (data?.usage) {
          setUsage(data.usage);
        }

        // Add to UI
        queryClient.setQueryData<AIMessage[]>(
          aiChatQueryKeys.messages(businessId, currentConvId),
          (old) => [...(old || []), assistantMessage]
        );

        // Show toast notifications for tool call results
        showToolResultToasts(data?.toolCalls);

        // NOTE: We don't auto-dispatch show_user events anymore.
        // The button in AIMessage will handle navigation when user clicks it.
        // This gives user control over when navigation happens.

        // Invalidate caches if data was modified by tool calls
        const modifiedResources = getModifiedResources(data?.toolCalls);
        invalidateModifiedResources(queryClient, modifiedResources, businessId);
        emitDataModifiedEvent(modifiedResources, businessId);

        // Save to database (includes tool_results for proper conversation history)
        await supabase
          .from('ai_messages')
          .insert({
            id: assistantMessageId,
            conversation_id: currentConvId,
            role: 'assistant',
            content: assistantMessage.content,
            tool_calls: data?.toolCalls || null,
            tool_results: data?.toolResults || null,
          });

        // Update conversation summary
        queryClient.setQueryData<AIConversationSummary[]>(
          aiChatQueryKeys.conversations(businessId),
          (old) =>
            (old || []).map(c =>
              c.id === currentConvId
                ? { ...c, updatedAt: new Date().toISOString(), messageCount: c.messageCount + 2 }
                : c
            )
        );

        // Clear failed message on success
        setLastFailedMessage(null);
      } catch (err) {
        // Check if this was an abort (user stopped the request)
        const isAborted = err instanceof Error && err.name === 'AbortError';

        if (isAborted) {
          setWasStopped(true);
          setError('Request stopped');
          // Don't show toast for user-initiated stop
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(errorMessage);
        toast.error('AI Error', { description: errorMessage });

        const errorMessageId = crypto.randomUUID();
        const errorResponse: AIMessage = {
          id: errorMessageId,
          role: 'assistant',
          content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`,
          timestamp: new Date().toISOString(),
        };

        queryClient.setQueryData<AIMessage[]>(
          aiChatQueryKeys.messages(businessId, currentConvId),
          (old) => [...(old || []), errorResponse]
        );

        // Save error response to database
        await supabase
          .from('ai_messages')
          .insert({
            id: errorMessageId,
            conversation_id: currentConvId,
            role: 'assistant',
            content: errorResponse.content,
          });
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null; // Clear to prevent stale reference
      }
    },
    [businessId, accessToken, activeConversationId, messages, queryClient, processStreamingResponse]
  );

  // Keep ref updated with latest sendMessage to avoid stale closures
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // Helper to format tool results for display
  const formatToolResultSummary = (toolResults: AIToolResult[]): string => {
    return toolResults.map(r =>
      r.success
        ? `✓ ${r.message || 'Completed successfully'}`
        : `✗ ${r.message || 'Failed'}`
    ).join('\n');
  };

  // Confirm and execute pending tools (direct execution without LLM call)
  const confirmTools = useCallback(
    async (pendingTools: PendingTool[]) => {
      if (!activeConversationId || !businessId) return;

      try {
        // Call the direct confirmation endpoint
        const response = await passServerFetch('/api/ai-chat/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId: activeConversationId,
            businessId,
            confirmed: true
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Confirmation failed' }));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        const data = await response.json();

        // Update the last assistant message: remove pendingTools, add executed toolCalls
        queryClient.setQueryData<AIMessage[]>(
          aiChatQueryKeys.messages(businessId, activeConversationId),
          (old) => {
            if (!old?.length) return old;
            const updated = [...old];
            const lastIdx = updated.length - 1;
            const lastMsg = updated[lastIdx];

            if (lastMsg.role === 'assistant' && lastMsg.pendingTools) {
              updated[lastIdx] = {
                ...lastMsg,
                pendingTools: undefined,
                toolCalls: data.toolCalls,
                toolResults: data.toolResults,
                content: lastMsg.content + '\n\n' + formatToolResultSummary(data.toolResults)
              };
            }
            return updated;
          }
        );

        // Show toast notifications for tool call results
        showToolResultToasts(data.toolCalls);

        // Invalidate caches if data was modified by tool calls
        const modifiedResources = getModifiedResources(data.toolCalls);
        invalidateModifiedResources(queryClient, modifiedResources, businessId);
        emitDataModifiedEvent(modifiedResources, businessId);

        // Update the assistant message in database with tool results
        const lastMessages = queryClient.getQueryData<AIMessage[]>(
          aiChatQueryKeys.messages(businessId, activeConversationId)
        );
        const lastMsg = lastMessages?.[lastMessages.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          await supabase
            .from('ai_messages')
            .update({
              tool_calls: data.toolCalls,
              tool_results: data.toolResults,
              content: lastMsg.content
            })
            .eq('id', lastMsg.id);
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to execute action';
        toast.error('Action failed', { description: errorMessage });
      }
    },
    [activeConversationId, businessId, queryClient]
  );

  // Cancel pending tools without executing
  const cancelPendingTools = useCallback(async () => {
    if (!activeConversationId || !businessId) return;

    try {
      const response = await passServerFetch('/api/ai-chat/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: activeConversationId,
          businessId,
          confirmed: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to cancel' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Only update UI after server confirms cancellation
      queryClient.setQueryData<AIMessage[]>(
        aiChatQueryKeys.messages(businessId, activeConversationId),
        (old) => {
          if (!old?.length) return old;
          const updated = [...old];
          const lastIdx = updated.length - 1;
          const lastMsg = updated[lastIdx];

          if (lastMsg.role === 'assistant' && lastMsg.pendingTools) {
            updated[lastIdx] = {
              ...lastMsg,
              pendingTools: undefined,
              content: lastMsg.content + '\n\n*Action cancelled.*'
            };
          }
          return updated;
        }
      );

      toast('Action cancelled');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel action';
      toast.error('Cancel failed', { description: errorMessage });
    }
  }, [activeConversationId, businessId, queryClient]);

  // Send answers to structured questions
  const sendQuestionAnswers = useCallback(
    async (answers: AIQuestionAnswers, displayText: string) => {
      // Send message with both display text and raw answers
      await sendMessage(displayText, answers);
    },
    [sendMessage]
  );

  // Clear current conversation messages
  const clearMessages = useCallback(async () => {
    if (!businessId || !activeConversationId) return;

    try {
      // Delete all messages in the conversation
      const { error } = await supabase
        .from('ai_messages')
        .delete()
        .eq('conversation_id', activeConversationId);

      if (error) throw error;

      // Clear messages cache
      queryClient.setQueryData<AIMessage[]>(
        aiChatQueryKeys.messages(businessId, activeConversationId),
        []
      );

      // Update conversation summary
      queryClient.setQueryData<AIConversationSummary[]>(
        aiChatQueryKeys.conversations(businessId),
        (old) =>
          (old || []).map(c =>
            c.id === activeConversationId
              ? { ...c, messageCount: 0 }
              : c
          )
      );
    } catch (err) {
      console.error('Error clearing messages:', err);
      toast.error('Failed to clear messages');
    }
    setError(null);
  }, [businessId, activeConversationId, queryClient]);

  // Stop the current AI generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('[AI Chat] Stopping generation...');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  // Retry the last failed/stopped message
  const retryLastMessage = useCallback(() => {
    if (lastFailedMessage) {
      console.log('[AI Chat] Retrying last message:', lastFailedMessage);
      setError(null);
      setWasStopped(false);
      sendMessage(lastFailedMessage);
    }
  }, [lastFailedMessage, sendMessage]);

  // Fork a conversation from a specific message
  const forkConversation = useCallback(async (
    fromMessageId: string,
    newContent: string
  ): Promise<string | null> => {
    if (!businessId || !activeConversationId) return null;

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return null;

      // Find the message index
      const messageIndex = messages.findIndex(m => m.id === fromMessageId);
      if (messageIndex === -1) return null;

      // Get messages before the edited message (not including it)
      const messagesToCopy = messages.slice(0, messageIndex);

      // Create new forked conversation with auto-generated title
      const { data: newConv, error: convError } = await supabase
        .from('ai_conversations')
        .insert({
          business_id: businessId,
          user_id: userData.user.id,
          title: generateTitle(newContent),
          forked_from_conversation_id: activeConversationId,
          forked_from_message_id: fromMessageId,
        })
        .select()
        .single();

      if (convError) throw convError;

      // Copy previous messages to the new conversation
      if (messagesToCopy.length > 0) {
        const messagesToInsert = messagesToCopy.map(msg => ({
          conversation_id: newConv.id,
          role: msg.role,
          content: msg.content,
          tool_calls: msg.toolCalls || null,
          tool_results: msg.toolResults || null,
        }));

        const { error: msgError } = await supabase
          .from('ai_messages')
          .insert(messagesToInsert);

        if (msgError) throw msgError;
      }

      // Add new conversation to cache
      const newSummary: AIConversationSummary = {
        id: newConv.id,
        title: newConv.title,
        createdAt: newConv.created_at,
        updatedAt: newConv.updated_at,
        messageCount: messagesToCopy.length,
        forkedFromConversationId: activeConversationId,
        forkedFromMessageId: fromMessageId,
      };

      queryClient.setQueryData<AIConversationSummary[]>(
        aiChatQueryKeys.conversations(businessId),
        (old) => [newSummary, ...(old || [])]
      );

      // Pre-populate the new conversation's message cache with copied messages
      const copiedMessages: AIMessage[] = messagesToCopy.map(msg => ({
        id: crypto.randomUUID(), // New IDs for copied messages
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        toolCalls: msg.toolCalls,
        toolResults: msg.toolResults,
      }));

      queryClient.setQueryData<AIMessage[]>(
        aiChatQueryKeys.messages(businessId, newConv.id),
        copiedMessages
      );

      // Switch to new conversation
      setActiveConversationId(newConv.id);

      // Use ref to call latest sendMessage after a brief delay for state to settle
      setTimeout(() => {
        if (sendMessageRef.current) {
          sendMessageRef.current(newContent);
        }
      }, 50);

      return newConv.id;
    } catch (err) {
      console.error('Error forking conversation:', err);
      toast.error('Failed to create conversation branch');
      return null;
    }
  }, [businessId, activeConversationId, messages, queryClient]);

  // Resend or edit a message - always forks to create a fresh branch from that point
  const resendMessage = useCallback(async (
    message: AIMessage,
    newContent?: string
  ) => {
    const content = newContent || message.content;

    // Always fork - this creates a new conversation branch starting from this message
    // The original conversation stays intact
    await forkConversation(message.id, content);
  }, [forkConversation]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    confirmTools,
    cancelPendingTools,
    sendQuestionAnswers, // For structured question form submissions
    // Conversation management
    conversationSummaries: conversations,
    activeConversationId,
    createNewConversation,
    switchConversation,
    deleteConversation,
    isLoadingConversations,
    // Usage stats
    usage,
    // Stop and retry
    stopGeneration,
    retryLastMessage,
    canRetry: !!lastFailedMessage && (!!error || wasStopped),
    wasStopped,
    // Streaming
    streamingContent,
    // Context
    currentContext: getCurrentContext(),
    // Forking
    resendMessage,
    forkConversation,
    // Auto-accept mode
    autoAcceptEnabled,
    setAutoAcceptEnabled,
  };
};

export default useAIChat;
